import { useEffect, useState } from "react";
import { Divider, MenuItem, TextField, Typography } from "@mui/material";
import { fetchBanks } from "../../api/BankService";

const V2_TEXT_FIELDS = [
  ["project_location", "Project Location"],
  ["quantity", "Quantity"],
  ["load_kg", "Load (Kgs)"],
  ["machine_location", "Location of Machine Unit"],
  ["travel_height", "Travel Height (Approx)"],
  ["flooring_illuminations", "Flooring & Illuminations"],
  ["display_type", "Display"],
  ["required_well", "Required Well Measurement"],
  ["available_well", "Available Well Width/Depth"],
  ["car_clear", "Car Clear Measurement"],
  ["pit_depth", "Pit Depth"],
  ["overhead", "Over Head Measurement"],
  ["power_supply", "Power Supply"],
  ["additional_fixtures", "Additional Fixtures"],
  ["discounted_price", "Discounted Price"],
  ["delivery_schedule", "Delivery Schedule"],
  ["vvvf_drive", "VVVF Drive Make"],
];

// V2 is the default — an unset value means the prefill has not landed yet,
// not that the user picked V1.
export const resolveTemplateVersion = (value) => {
  if (value === undefined || value === null || value === "") return 2;
  return Number(value) === 1 ? 1 : 2;
};

// PDF template picker — rendered at the top of the quotation form grid.
export const QuotationTemplateSelect = ({ quotation, setQuotation }) => (
  <TextField
    select
    label="PDF Template"
    size="small"
    fullWidth
    value={resolveTemplateVersion(quotation.template_version)}
    onChange={(e) =>
      setQuotation({ ...quotation, template_version: Number(e.target.value) })
    }
    sx={{ gridColumn: "1 / -1" }}
  >
    <MenuItem value={1}>V1 — Classic</MenuItem>
    <MenuItem value={2}>V2 — Detailed (Specifications & Contract)</MenuItem>
  </TextField>
);

// Sensor is free text; the remaining feature flags stay Yes/No. Both apply
// to the V1 and V2 templates alike.
export const QuotationSensorFields = ({ quotation, setQuotation }) => {
  const boolSelect = (key, label) => (
    <TextField
      select
      label={label}
      id={key}
      fullWidth
      size="small"
      value={quotation[key] ? 1 : 0}
      onChange={(e) =>
        setQuotation({ ...quotation, [key]: Number(e.target.value) === 1 })
      }
    >
      <MenuItem value={0}>No</MenuItem>
      <MenuItem value={1}>Yes</MenuItem>
    </TextField>
  );

  return (
    <>
      <TextField
        label="Sensor"
        id="sensor"
        fullWidth
        size="small"
        value={quotation.sensor || ""}
        onChange={(e) =>
          setQuotation({ ...quotation, sensor: e.target.value })
        }
      />
      {boolSelect("overload_sensor", "Overload Sensor")}
      {boolSelect("biometric", "Bio Metric")}
    </>
  );
};

// Extra fields used only by the V2 (detailed) template. Renders nothing on V1.
const QuotationV2Fields = ({ quotation, setQuotation, errors = {} }) => {
  const [banks, setBanks] = useState([]);
  const isV2 = resolveTemplateVersion(quotation.template_version) === 2;

  useEffect(() => {
    if (!isV2 || banks.length) return;
    fetchBanks()
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setBanks(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        // silent — bank selection stays empty
      });
  }, [isV2, banks.length]);

  if (!isV2) return null;

  const handleChange = (key) => (e) =>
    setQuotation({ ...quotation, [key]: e.target.value });
  const getError = (key) => !!errors[key];
  const getHelperText = (key) =>
    Array.isArray(errors[key]) ? errors[key][0] : errors[key];

  return (
    <>
      <Divider sx={{ gridColumn: "1 / -1", mt: 1 }} />
      <Typography
        variant="subtitle2"
        fontWeight="700"
        sx={{ gridColumn: "1 / -1" }}
      >
        V2 Template Details
      </Typography>
      {V2_TEXT_FIELDS.map(([key, label]) => (
        <TextField
          key={key}
          label={label}
          id={key}
          fullWidth
          size="small"
          value={quotation[key] || ""}
          onChange={handleChange(key)}
          error={getError(key)}
          helperText={
            key === "power_supply"
              ? getHelperText(key) || "Use | to split Machine Room / Lift Well lines"
              : getHelperText(key)
          }
        />
      ))}
      <TextField
        select
        label="Bank (Account Details on PDF)"
        id="bank_id"
        fullWidth
        size="small"
        value={quotation.bank_id || ""}
        onChange={(e) =>
          setQuotation({ ...quotation, bank_id: e.target.value || null })
        }
        error={getError("bank_id")}
        helperText={getHelperText("bank_id")}
      >
        <MenuItem value="">None</MenuItem>
        {banks.map((bank) => (
          <MenuItem key={bank.id} value={bank.id}>
            {bank.name}
            {bank.branch ? ` — ${bank.branch}` : ""}
          </MenuItem>
        ))}
      </TextField>
    </>
  );
};

export default QuotationV2Fields;
